import React, { useState, useEffect, useRef, useMemo } from 'react';
import PAGE_INDEX from '../data/pageIndex';
import { RESOURCES } from '../data/apiReference';
import './SearchBox.css';

// ── Build flat search corpus from static page index ───────────────────────────

function buildCorpus() {
  const items = [];
  for (const page of PAGE_INDEX) {
    // Page-level entry (matches on title + description)
    items.push({
      type:      'page',
      pageId:    page.id,
      pageLabel: page.label,
      group:     page.group,
      section:   page.label,
      sectionId: null,
      text:      page.description ?? '',
    });
    // Section-level entries
    for (const sec of page.sections ?? []) {
      items.push({
        type:      'page',
        pageId:    page.id,
        pageLabel: page.label,
        group:     page.group,
        section:   sec.label,
        sectionId: sec.id,
        text:      sec.text ?? '',
      });
    }
  }

  // ── API reference resources & endpoints ────────────────────────────────────
  // Each resource gets a top-level entry; each endpoint a section-level one
  // anchored to its `id` so clicking jumps straight to it.
  for (const [resourceId, resource] of Object.entries(RESOURCES)) {
    items.push({
      type:      'api-resource',
      pageId:    'api-reference',
      pageLabel: 'API Reference',
      group:     'API',
      section:   resource.label,
      sectionId: resourceId,
      text:      [resource.tagline, resource.description].filter(Boolean).join(' '),
    });

    for (const ep of resource.endpoints ?? []) {
      items.push({
        type:           'api-endpoint',
        pageId:         'api-reference',
        pageLabel:      `API Reference › ${resource.label}`,
        group:          'API',
        section:        `${ep.method} ${ep.path}`,
        sectionId:      resourceId,
        endpointAnchor: ep.id,
        text:           [ep.title, ep.description, ep.path].filter(Boolean).join(' '),
      });
    }
  }

  return items;
}

const CORPUS = buildCorpus();

// ── Excerpt + highlight ───────────────────────────────────────────────────────

function excerpt(text, query, maxLen = 110) {
  const lo = text.toLowerCase();
  const qi = lo.indexOf(query.toLowerCase());
  if (qi === -1) return text.slice(0, maxLen) + (text.length > maxLen ? '…' : '');
  const start = Math.max(0, qi - 40);
  const end   = Math.min(text.length, qi + query.length + 65);
  return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
}

function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="s-mark">{p}</mark>
          : p
      )}
    </>
  );
}

// ── SearchBox ─────────────────────────────────────────────────────────────────

export default function SearchBox({ onNavigate }) {
  const [open,     setOpen]     = useState(false);
  const [query,    setQuery]    = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  const index = useMemo(() => CORPUS, []);

  // Cmd+F / Ctrl+F
  useEffect(() => {
    function onKey(e) {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Results
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q.length < 2) return [];

    const seen = new Set();
    const hits = [];

    for (const seg of index) {
      const key = `${seg.pageId}::${seg.section}`;
      if (seen.has(key)) continue;

      const inSection = seg.section.toLowerCase().includes(q);
      const inText    = seg.text.toLowerCase().includes(q);
      const inGroup   = seg.group?.toLowerCase().includes(q);

      if (inSection || inText || inGroup) {
        seen.add(key);
        hits.push({ ...seg, score: inSection ? 2 : 1 });
      }
    }

    return hits.sort((a, b) => b.score - a.score).slice(0, 10);
  }, [query, index]);

  useEffect(() => { setSelected(0); }, [results]);

  function navigate(result) {
    const { pageId, sectionId, endpointAnchor } = result;

    if (pageId === 'api-reference' && sectionId) {
      onNavigate(`${pageId}/${sectionId}`);
      // For endpoint results, scroll to the specific endpoint anchor after
      // the resource page has rendered.
      if (endpointAnchor) {
        setTimeout(() => {
          document.getElementById(endpointAnchor)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 80);
      }
    } else {
      onNavigate(pageId, sectionId ?? undefined);
    }
    setOpen(false);
  }

  function onInputKey(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (results[selected]) navigate(results[selected]); }
    if (e.key === 'Escape')    { setOpen(false); }
  }

  const q = query.trim();

  return (
    <>
      {/* Trigger button in topbar */}
      <button className="s-trigger" onClick={() => setOpen(true)}>
        <span className="s-trigger-icon">⌕</span>
        <span className="s-trigger-text">Search docs...</span>
        <kbd className="s-trigger-kbd">/</kbd>
      </button>

      {/* Modal */}
      {open && (
        <div className="s-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="s-modal">
            {/* Input row */}
            <div className="s-input-row">
              <span className="s-input-icon">⌕</span>
              <input
                ref={inputRef}
                className="s-input"
                type="text"
                placeholder="Search docs..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onInputKey}
                autoComplete="off"
                spellCheck={false}
              />
              {results.length > 0
                ? <kbd className="s-esc-hint">ESC</kbd>
                : <kbd className="s-esc-hint" onClick={() => setOpen(false)}>ESC</kbd>
              }
            </div>

            {/* Results */}
            {results.length > 0 && (
              <div className="s-results">
                {results.map((r, i) => {
                  const breadcrumb = r.sectionId
                    ? `${r.group} › ${r.pageLabel} › ${r.section}`
                    : `${r.group} › ${r.section}`;
                  return (
                    <button
                      key={`${r.pageId}::${r.section}::${i}`}
                      className={`s-result${i === selected ? ' s-result--active' : ''}`}
                      onMouseEnter={() => setSelected(i)}
                      onMouseDown={(e) => { e.preventDefault(); navigate(r); }}
                    >
                      <div className="s-result-breadcrumb">{breadcrumb}</div>
                      <div className="s-result-title">
                        <span className="s-page-hash">#</span>
                        <Highlight text={r.section} query={q} />
                      </div>
                      <div className="s-result-excerpt">
                        <Highlight text={excerpt(r.text, q)} query={q} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {q.length >= 2 && results.length === 0 && (
              <div className="s-empty">No results for <strong>"{q}"</strong></div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
